import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import SaveIcon from '@mui/icons-material/Save';
import { SubmitEvent, useCallback, useState } from 'react';
import { FormLabel, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { red } from '../../theme/themePrimitives';
import OverheadCostLineItemForm, { OverheadCostLineItemFormState } from './OverheadCostLineItemForm';
import { type OverheadCostLineItem, validate as validateOverheadCostLineItem } from '../../models/OverheadCostLineItem';

export interface OverheadCostFormProps {
  initialItems?: OverheadCostLineItem[];
  onSubmit: (overheadCostLineItems: OverheadCostLineItem[]) => Promise<void>;
}

export default function OverheadCostForm(props: OverheadCostFormProps) {
  const {
    initialItems = [],
    onSubmit
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overheadCostLineItems, setOverheadCostLineItems] = useState<OverheadCostLineItem[]>(initialItems);
  const [overheadCostLineItemsErrors, setOverheadCostLineItemsErrors] = useState<Map<number, Partial<Record<keyof OverheadCostLineItemFormState['values'], string>>>>(new Map());

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);

      const lineItemErrors = new Map<number, Partial<Record<keyof OverheadCostLineItemFormState['values'], string>>>();

      if ((overheadCostLineItems || []).length > 0) {
        for (let index = 0; index < overheadCostLineItems.length; index++) {
          const overheadCostLineItem = overheadCostLineItems[index];

          const { issues } = validateOverheadCostLineItem(overheadCostLineItem);

          let errors = null;

          if (issues && issues.length > 0) {
            errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
          }

          if (errors) {
            lineItemErrors.set(index, errors);
          }
        }
      }

      setOverheadCostLineItemsErrors(
        lineItemErrors);

      if (lineItemErrors.size > 0) {
        setIsSubmitting(false);
        return;
      }

      try {
        await onSubmit(overheadCostLineItems);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, overheadCostLineItems],
  );

  const handleOverheadCostLineItemFormFieldChange = useCallback(
    (updatedIngredient: Partial<OverheadCostLineItemFormState['values']>, index: number) => {
      const updatedOverheadCostLineItems = [...overheadCostLineItems];

      updatedOverheadCostLineItems[index] = { ...overheadCostLineItems[index], ...updatedIngredient };

      setOverheadCostLineItems(updatedOverheadCostLineItems);
    },
    [overheadCostLineItems],
  );

  const addNewOverheadCostLineItem = useCallback(() => {
    setOverheadCostLineItems(
      [...overheadCostLineItems, {
        id: 0,
        description: '',
        totalCost: 0,
        isActive: true
      }]);
  }, [overheadCostLineItems]);

  const removeOverheadCostLineItem = useCallback((indexToRemove: number) => {
    const updatedOverheadCostLineItems = overheadCostLineItems.filter((_, index) => index !== indexToRemove);
    setOverheadCostLineItems(updatedOverheadCostLineItems);
  }, [overheadCostLineItems]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid spacing={0} sx={{ mb: 2, width: '100%' }}>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <FormLabel>
              Overhead Cost Line Items
            </FormLabel>
            <div>
              <Button
                type="button"
                color="secondary"
                startIcon={<Add />}
                variant="outlined"
                onClick={addNewOverheadCostLineItem}>
                Add Overhead Cost Line Item
              </Button>
            </div>
          </Stack>
        </Grid>
        <Grid container spacing={1} sx={{ mb: 2, pt: 2, width: '100%' }}>
          <Grid spacing={0} sx={{ width: '90%' }}>
            {overheadCostLineItems?.map((overheadCostLineItem: OverheadCostLineItem, index: number) => (
              <Stack key={index} direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                <OverheadCostLineItemForm
                  initialValue={overheadCostLineItem}
                  initialErrors={overheadCostLineItemsErrors.get(index)}
                  onFormValuesUpdate={(itemFormValues) => handleOverheadCostLineItemFormFieldChange(itemFormValues, index)} />
                <Box sx={{ width: '36px' }}>
                  <IconButton
                    size="small"
                    style={{ marginTop: '8px' }}
                    onClick={() => removeOverheadCostLineItem(index)}>
                    <Delete htmlColor={red[400]} />
                  </IconButton>
                </Box>
              </Stack>
            ))}
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Box></Box>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
}
